import { useCallback, useEffect, useMemo } from 'react'
import { reportStationDetailsEvent } from '@metro/station-details'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * Host-side analytics wiring for `<StationDetails>`. Wraps the four
 * `on*` callbacks the host already passes to the component so that each
 * user action ALSO emits its §8 contract event through the project's
 * {@link MetroAnalyticsReporter}, and fires `_opened` once per
 * resolved-station mount.
 *
 * Why a host-side hook (not inside the package):
 *  - The `@metro/station-details` package is intentionally
 *    stateless + domain-agnostic (`feedback_reusable_module_domain_
 *    agnostic`) — it never imports an analytics backend. Analytics
 *    wiring is the host's job, same as on every other V2 module
 *    (Suggester takes an `onAnalytics` prop the host fills with a
 *    `MetroAnalyticsReporter`; Routing uses a context provider).
 *  - Pre-Task #12 the facade `reportStationDetailsEvent` was
 *    exported from the package but had ZERO call sites in the host —
 *    `_opened`, `_favorite_tapped`, `_transfer_tapped`, `_paywall_
 *    tapped`, `_report_tapped` were all dark at runtime. This hook
 *    closes that gap without coupling the module to a backend.
 *
 * §8 cross-platform contract (byte-identical with iOS / Android):
 *   station_details_opened                 {station_id}
 *   station_details_transfer_tapped        {from_id, to_id}
 *   station_details_favorite_tapped        {station_id, to_state}
 *   station_details_paywall_tapped         {station_id}
 *   station_details_report_tapped          {station_id}
 *   station_details_timetable_upsell_shown {station_id}
 *
 * The reporter shape adapter folds the facade's typed
 * `{name, parameters}` event into the project's reporter signature
 * `(event: string, metadata: Record<string, unknown>) => void` once,
 * inline, so callers only see the project type.
 */
export interface StationDetailsAnalyticsInput {
  /**
   * Currently-resolved station id (null = no station). Drives the
   * `_opened` emission: fires once each time the id transitions from
   * null/different → a non-null value (next station opened).
   */
  readonly stationId: string | null
  /** Project-level reporter — the host passes `realMetroAnalyticsReporter`
   *  (forwards to GA4 + Amplitude); tests inject a spy. */
  readonly reporter: MetroAnalyticsReporter
  /** Pass-through host actions; each is invoked first, then the analytics emit fires. */
  readonly onFavoriteTap: () => void
  readonly onTransferTap: (transferId: string) => void
  readonly onPaywallTap: () => void
  readonly onReportIssueTap: () => void
  /**
   * Current favourite state — needed so `_favorite_tapped {to_state}`
   * carries the post-toggle value (matches iOS, which emits after the
   * state flip).
   */
  readonly isFavorite: boolean
  /**
   * Premium gate — mirrors the package's `isPremium` prop. The upsell
   * banner (`TimetableUpsellBanner`) renders only when `!isPremium`;
   * `_timetable_upsell_shown` fires once per resolved-station appearance
   * of that banner, matching iOS `.onAppear` (non-premium only).
   */
  readonly isPremium: boolean
}

export interface StationDetailsAnalyticsWiring {
  readonly onFavoriteTap: () => void
  readonly onTransferTap: (transferId: string) => void
  readonly onPaywallTap: () => void
  readonly onReportIssueTap: () => void
}

export function useStationDetailsAnalytics(
  input: StationDetailsAnalyticsInput,
): StationDetailsAnalyticsWiring {
  const {
    stationId,
    reporter,
    onFavoriteTap,
    onTransferTap,
    onPaywallTap,
    onReportIssueTap,
    isFavorite,
    isPremium,
  } = input

  // Adapter: facade emits `{name, parameters}`; the project reporter is
  // `(name, metadata) => void`. Stable per-reporter so memoised wrappers
  // stay referentially stable across renders.
  const facadeReporter = useMemo(
    () => (evt: { name: string; parameters: Record<string, string | number | boolean> }) =>
      reporter(evt.name, evt.parameters),
    [reporter],
  )

  // `_opened` once per station — re-fires when the user opens a different
  // station (transferTapped flips `stationId` without dismissing the sheet).
  useEffect(() => {
    if (stationId === null) return
    reportStationDetailsEvent(facadeReporter, { kind: 'opened', stationId })
  }, [stationId, facadeReporter])

  // `_timetable_upsell_shown` once per appearance of the non-premium upsell
  // banner — mirrors iOS `TimetableUpsellBanner.onAppear` (the banner only
  // renders when `!isPremium`). Re-fires per station, in lockstep with
  // `_opened`, since the banner re-mounts when a different station opens.
  useEffect(() => {
    if (stationId === null || isPremium) return
    reportStationDetailsEvent(facadeReporter, { kind: 'timetableUpsellShown', stationId })
  }, [stationId, isPremium, facadeReporter])

  const favoriteTap = useCallback((): void => {
    onFavoriteTap()
    if (stationId === null) return
    reportStationDetailsEvent(facadeReporter, {
      kind: 'favoriteTapped',
      stationId,
      // iOS / Android emit AFTER the state flip — the `to_state` is the
      // value the host is transitioning into, i.e. !current.
      toState: !isFavorite,
    })
  }, [onFavoriteTap, facadeReporter, stationId, isFavorite])

  const transferTap = useCallback(
    (transferId: string): void => {
      onTransferTap(transferId)
      if (stationId === null) return
      reportStationDetailsEvent(facadeReporter, {
        kind: 'transferTapped',
        fromId: stationId,
        toId: transferId,
      })
    },
    [onTransferTap, facadeReporter, stationId],
  )

  const paywallTap = useCallback((): void => {
    onPaywallTap()
    if (stationId === null) return
    reportStationDetailsEvent(facadeReporter, { kind: 'paywallTapped', stationId })
  }, [onPaywallTap, facadeReporter, stationId])

  const reportIssueTap = useCallback((): void => {
    onReportIssueTap()
    if (stationId === null) return
    reportStationDetailsEvent(facadeReporter, { kind: 'reportIssueTapped', stationId })
  }, [onReportIssueTap, facadeReporter, stationId])

  return {
    onFavoriteTap: favoriteTap,
    onTransferTap: transferTap,
    onPaywallTap: paywallTap,
    onReportIssueTap: reportIssueTap,
  }
}
